// modules/transactions/ui.ts — <app-panel-registro> + <app-panel-historial>

import { bus }       from '../../core/event-bus';
import { getModule } from '../../core/registry';
import { useBus }    from '../../shared/hooks/use-bus';
import { formatCurrency, formatRelativeDate, parseDate, getPeriodDates } from '../../shared/utils/format';
import { AppModal }  from '../../shared/components/modal';
import type { Transaction, ParsedTransaction } from '../../shared/types/global';

// ── Panel Registro ──────────────────────────────────────────────────────────

export class AppPanelRegistro extends HTMLElement {
  private _editingId: string | null = null;
  private _modal!: AppModal;

  static define() { customElements.define('app-panel-registro', AppPanelRegistro); }

  connectedCallback() {
    this.render();
    this._modal = this.querySelector('app-modal') as AppModal;
    this.setupForm();
    this.setupVoice();

    useBus('transactions:saved',   () => { this.resetForm(); this.refreshLastTx(); }, this);
    useBus('transactions:updated', () => { this.resetForm(); this.refreshLastTx(); }, this);
    useBus('transactions:deleted', () => this.refreshLastTx(), this);
    useBus('voice:result',         ({ text }) => this.onTranscript(text), this);
    useBus('voice:parsed',         ({ parsed }) => this.fillForm(parsed), this);
    useBus('voice:started',        () => this.setMicState('recording'), this);
    useBus('voice:stopped',        () => this.setMicState('idle'), this);
    useBus('voice:error',          ({ message }) => this.setStatus(message, 'error'), this);
    useBus('categories:updated',   () => this.populateCats(), this);
    useBus('transactions:loaded',  () => this.refreshLastTx(), this);

    this.populateCats();
    this.refreshLastTx();
  }

  private render() {
    this.innerHTML = `
      <style>
        .ai-wrap { display:flex; flex-direction:column; align-items:center; padding: var(--space-4) 0; gap: var(--space-3); }
        .mic-btn {
          width: 64px; height: 64px; border-radius: 50%;
          background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
          border: none; font-size: 1.8rem; cursor: pointer;
          transition: transform var(--duration-fast), box-shadow var(--duration-normal);
          box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-primary) 40%, transparent);
        }
        .mic-btn.recording {
          background: linear-gradient(135deg, var(--color-error), var(--color-error-dark));
          animation: pulse 1.2s infinite;
        }
        @keyframes pulse {
          0%,100% { box-shadow: 0 0 0 0 color-mix(in srgb, var(--color-error) 50%, transparent); }
          50%      { box-shadow: 0 0 0 16px transparent; }
        }
        .mic-btn:disabled { opacity: .4; cursor: not-allowed; }
        .text-input-row { display:flex; gap: var(--space-2); width:100%; }
        .text-input-row input {
          flex:1; background: var(--color-bg); border: 1px solid var(--color-border);
          border-radius: var(--radius-md); padding: var(--space-3);
          color: var(--color-text); font-family: var(--font-body); font-size: var(--text-sm);
        }
        .text-input-row input:focus { outline: none; border-color: var(--color-border-focus); }
        .send-btn {
          background: linear-gradient(135deg, var(--color-primary), var(--color-accent));
          border: none; border-radius: var(--radius-md); padding: 0 var(--space-4);
          font-size: 1.1rem; cursor: pointer; color: white;
        }
        .status { font-size: var(--text-xs); color: var(--color-text-muted); min-height: 1.2em; text-align:center; }
        .status.error { color: var(--color-error); }
        .mini-spin { display:inline-block; width:12px; height:12px; vertical-align:-2px;
          border-radius:50%; border:2px solid var(--color-border); border-top-color: var(--color-primary);
          animation: hspin .7s linear infinite; margin-right:6px; }
        @keyframes hspin { to { transform: rotate(360deg); } }
        .card { background: var(--color-surface); border-radius: var(--radius-lg);
                padding: var(--space-4); margin-bottom: var(--space-3);
                border: 1px solid var(--color-border); }
        .tipo-toggle { display:grid; grid-template-columns:1fr 1fr; gap: var(--space-2); margin-bottom: var(--space-3); }
        .tipo-opt {
          text-align:center; padding: var(--space-3); border-radius: var(--radius-md);
          border: 1px solid var(--color-border); cursor:pointer; font-weight: var(--font-weight-semibold);
          transition: all var(--duration-fast);
        }
        .tipo-opt.gasto   { background: var(--color-expense-subtle); border-color: var(--color-expense); color: var(--color-expense); }
        .tipo-opt.ingreso { background: var(--color-income-subtle);  border-color: var(--color-income);  color: var(--color-income);  }
        .form-grid  { display:grid; grid-template-columns:1fr 1fr; gap: var(--space-2); }
        .form-field label { font-size: var(--text-xs); color: var(--color-text-muted); display:block; margin-bottom: var(--space-1); }
        .form-field input, .form-field select {
          width:100%; background: var(--color-bg); border: 1px solid var(--color-border);
          border-radius: var(--radius-md); padding: var(--space-2) var(--space-3);
          color: var(--color-text); font-family: var(--font-body); font-size: var(--text-sm);
        }
        .form-field input:focus, .form-field select:focus { outline:none; border-color: var(--color-border-focus); }
        .btn-save {
          width:100%; margin-top: var(--space-4);
          background: var(--color-success); color: white; border:none;
          border-radius: var(--radius-md); padding: var(--space-3);
          font-family: var(--font-body); font-size: var(--text-base);
          font-weight: var(--font-weight-semibold); cursor: pointer;
          transition: opacity var(--duration-fast);
        }
        .btn-save:hover { opacity: .9; }
        .tx-item { display:flex; justify-content:space-between; align-items:center;
                   padding: var(--space-3); border-radius: var(--radius-md);
                   background: var(--color-bg); margin-bottom: var(--space-2);
                   border: 1px solid var(--color-border); }
        .tx-left  { flex:1; min-width:0; }
        .tx-desc  { font-weight: var(--font-weight-medium); font-size: var(--text-sm);
                    white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .tx-meta  { font-size: var(--text-xs); color: var(--color-text-muted); margin-top: 2px; }
        .tx-right { display:flex; align-items:center; gap: var(--space-2); flex-shrink:0; }
        .tx-amount { font-family: var(--font-display); font-weight: var(--font-weight-semibold);
                     font-variant-numeric: tabular-nums; font-size: var(--text-base); }
        .tx-amount.gasto   { color: var(--color-expense); }
        .tx-amount.ingreso { color: var(--color-income);  }
        .sync-badge { font-size: 0.65rem; }
        .btn-icon { background:none; border:none; cursor:pointer; padding: var(--space-1);
                    border-radius: var(--radius-sm); color: var(--color-text-muted);
                    transition: color var(--duration-fast); font-size: 0.9rem; }
        .btn-icon:hover { color: var(--color-text); }
        .section-title { font-size: var(--text-sm); font-weight: var(--font-weight-semibold);
                         margin-bottom: var(--space-2); color: var(--color-text-muted); }
      </style>

      <!-- Entrada IA -->
      <div class="ai-wrap">
        <button class="mic-btn" id="micBtn" title="Hablar">🎤</button>
        <div class="text-input-row">
          <input type="text" id="aiInput" placeholder="O escribí: gasto 500 super" enterkeyhint="send">
          <button class="send-btn" id="sendBtn" title="Enviar">✨</button>
        </div>
        <p class="status" id="status">Tocá el micrófono o escribí en lenguaje natural</p>
      </div>

      <!-- Formulario -->
      <div class="card">
        <form id="txForm">
          <div class="tipo-toggle">
            <div class="tipo-opt gasto"   data-tipo="Gasto">Gasto</div>
            <div class="tipo-opt"         data-tipo="Ingreso">Ingreso</div>
          </div>
          <input type="hidden" id="fTipo" value="Gasto">
          <div class="form-grid">
            <div class="form-field">
              <label>Monto</label>
              <input type="number" id="fMonto" step="0.01" inputmode="decimal" placeholder="0.00" required>
            </div>
            <div class="form-field">
              <label>Descripción</label>
              <input type="text" id="fDesc" placeholder="Ej: Café">
            </div>
          </div>
          <div class="form-grid" style="margin-top: var(--space-2)">
            <div class="form-field">
              <label>Categoría</label>
              <select id="fCat"></select>
            </div>
            <div class="form-field">
              <label>Subcategoría</label>
              <select id="fSub"></select>
            </div>
          </div>
          <div class="form-field" style="margin-top: var(--space-2)">
            <label>Fecha</label>
            <input type="date" id="fFecha">
          </div>
          <button type="submit" class="btn-save" id="btnSave">Guardar</button>
        </form>
      </div>

      <!-- Últimos movimientos -->
      <p class="section-title">Últimos movimientos</p>
      <div id="lastTx"></div>

      <app-modal></app-modal>
    `;
  }

  /** yyyy-mm-dd de HOY en hora local (para el value del <input type="date">). */
  private todayInputValue(): string {
    const d = new Date();
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }

  private setDate(value: string) {
    (this.querySelector('#fFecha') as HTMLInputElement).value = value;
  }

  private setupForm() {
    this.setDate(this.todayInputValue());

    // Tipo toggle
    this.querySelectorAll('.tipo-opt').forEach(opt => {
      opt.addEventListener('click', () => {
        const tipo = (opt as HTMLElement).dataset.tipo as 'Gasto' | 'Ingreso';
        this.setTipo(tipo);
      });
    });

    // Submit
    this.querySelector('#txForm')!.addEventListener('submit', async (e) => {
      e.preventDefault();
      const tipo         = (this.querySelector('#fTipo') as HTMLInputElement).value as 'Gasto' | 'Ingreso';
      const monto        = parseFloat((this.querySelector('#fMonto') as HTMLInputElement).value);
      const descripcion  = (this.querySelector('#fDesc') as HTMLInputElement).value;
      const categoria    = (this.querySelector('#fCat') as HTMLSelectElement).value;
      const subcategoria = (this.querySelector('#fSub') as HTMLSelectElement).value;
      const fechaVal     = (this.querySelector('#fFecha') as HTMLInputElement).value;
      // yyyy-mm-dd del input → ISO local sin Z (medianoche), evita el corrimiento de día en UTC-3.
      const fecha        = fechaVal ? `${fechaVal}T00:00:00` : undefined;

      if (!monto || monto <= 0) return;

      interface TxInput { tipo: 'Gasto' | 'Ingreso'; monto: number; descripcion: string; categoria: string; subcategoria: string; fecha?: string; }
      const tx = getModule<{ save: (d: TxInput) => Promise<void>; update: (id: string, d: Partial<TxInput>) => Promise<void> }>('transactions');
      if (this._editingId) {
        await tx?.update(this._editingId, { tipo, monto, descripcion, categoria, subcategoria, fecha });
      } else {
        await tx?.save({ tipo, monto, descripcion, categoria, subcategoria, fecha });
      }
    });
  }

  private setupVoice() {
    const micBtn  = this.querySelector<HTMLButtonElement>('#micBtn')!;
    const sendBtn = this.querySelector<HTMLButtonElement>('#sendBtn')!;
    const input   = this.querySelector<HTMLInputElement>('#aiInput')!;

    micBtn.addEventListener('click', () => {
      const voice = getModule<{ startListening: () => void; stopListening: () => void; isSupported: () => boolean }>('voice');
      if (!voice?.isSupported()) {
        this.setStatus('Tu navegador no soporta entrada por voz', 'error');
        return;
      }
      if (micBtn.classList.contains('recording')) {
        voice.stopListening();
      } else {
        voice.startListening();
      }
    });

    const sendText = () => {
      const text = input.value.trim();
      if (!text) return;
      this.setProcessing('Interpretando…');
      const voice = getModule<{ parseText: (t: string) => Promise<unknown> }>('voice');
      voice?.parseText(text)
        .then(parsed => bus.emit('voice:parsed', { parsed: parsed as never }))
        .catch(err  => this.setStatus(err.message, 'error'));
    };

    sendBtn.addEventListener('click', sendText);
    input.addEventListener('keydown', (e: KeyboardEvent) => { if (e.key === 'Enter') { e.preventDefault(); sendText(); } });
  }

  private fillForm(parsed: ParsedTransaction) {
    if (parsed.tipo) this.setTipo(parsed.tipo);
    const monto = parsed.monto != null ? Number(parsed.monto) : NaN;
    if (!Number.isNaN(monto) && monto > 0) (this.querySelector('#fMonto') as HTMLInputElement).value = String(monto);
    if (parsed.descripcion) (this.querySelector('#fDesc') as HTMLInputElement).value = parsed.descripcion;

    const catSel = this.querySelector<HTMLSelectElement>('#fCat')!;
    if (parsed.categoria && catSel.querySelector(`option[value="${parsed.categoria}"]`)) {
      catSel.value = parsed.categoria;
      this.updateSubs();
      if (parsed.subcategoria) {
        const subSel = this.querySelector<HTMLSelectElement>('#fSub')!;
        if (subSel.querySelector(`option[value="${parsed.subcategoria}"]`)) {
          subSel.value = parsed.subcategoria;
        }
      }
    }

    // Fecha resuelta por la IA (yyyy-mm-dd) → input date. Si no vino, queda hoy.
    if (parsed.fecha && /^\d{4}-\d{2}-\d{2}$/.test(parsed.fecha)) {
      this.setDate(parsed.fecha);
    }

    this.setStatus('Datos cargados. Revisá y guardá ✓');
    (this.querySelector('#aiInput') as HTMLInputElement).value = '';
  }

  private setTipo(tipo: 'Gasto' | 'Ingreso') {
    (this.querySelector('#fTipo') as HTMLInputElement).value = tipo;
    this.querySelectorAll('.tipo-opt').forEach(opt => {
      opt.classList.remove('gasto', 'ingreso');
    });
    this.querySelector(`[data-tipo="${tipo}"]`)?.classList.add(tipo === 'Gasto' ? 'gasto' : 'ingreso');
  }

  private setMicState(state: 'idle' | 'recording') {
    const btn = this.querySelector<HTMLButtonElement>('#micBtn')!;
    btn.textContent = state === 'recording' ? '⏹️' : '🎤';
    btn.classList.toggle('recording', state === 'recording');
    this.setStatus(state === 'recording' ? 'Escuchando…' : 'Tocá el micrófono o escribí en lenguaje natural');
  }

  private setStatus(msg: string, type: 'normal' | 'error' = 'normal') {
    const el = this.querySelector('#status')!;
    el.textContent = msg;
    el.classList.toggle('error', type === 'error');
  }

  /** Muestra un spinner + mensaje mientras la IA procesa. */
  private setProcessing(msg: string) {
    const el = this.querySelector('#status')!;
    el.classList.remove('error');
    el.innerHTML = `<span class="mini-spin"></span>${msg}`;
  }

  /** Lo que el micrófono escuchó: lo mostramos en el cuadro y marcamos "procesando". */
  private onTranscript(text: string) {
    (this.querySelector('#aiInput') as HTMLInputElement).value = text;
    this.setProcessing('Interpretando…');
  }

  private populateCats() {
    const cats = getModule<{ getAll: () => Record<string, string[]> }>('categories')?.getAll() ?? {};
    const catSel = this.querySelector<HTMLSelectElement>('#fCat')!;
    catSel.innerHTML = Object.keys(cats).map(c => `<option value="${c}">${c}</option>`).join('');
    catSel.removeEventListener('change', this.updateSubs);
    catSel.addEventListener('change', this.updateSubs.bind(this));
    this.updateSubs();
  }

  private updateSubs() {
    const cats = getModule<{ getAll: () => Record<string, string[]> }>('categories')?.getAll() ?? {};
    const cat  = (this.querySelector('#fCat') as HTMLSelectElement)?.value ?? '';
    const subs = cats[cat] ?? [];
    const subSel = this.querySelector<HTMLSelectElement>('#fSub')!;
    subSel.innerHTML = subs.map((s: string) => `<option value="${s}">${s}</option>`).join('');
  }

  private refreshLastTx() {
    const txs = getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll().slice(0, 10) ?? [];
    const container = this.querySelector('#lastTx')!;
    if (!txs.length) {
      container.innerHTML = '<app-empty-state icon="💸" message="Sin movimientos aún" sub="Registrá tu primer gasto arriba"></app-empty-state>';
      return;
    }
    container.innerHTML = txs.map(tx => this.txHtml(tx)).join('');
    this.bindTxActions();
  }

  private txHtml(tx: Transaction): string {
    const sign  = tx.tipo === 'Gasto' ? '−' : '+';
    const cls   = tx.tipo === 'Gasto' ? 'gasto' : 'ingreso';
    const badge = tx.syncStatus === 'synced' ? '☁️✓' : tx.syncStatus === 'pending' ? '☁️⚠' : '☁️⟳';
    return `
      <div class="tx-item" data-id="${tx.id}">
        <div class="tx-left">
          <p class="tx-desc">${tx.descripcion || tx.subcategoria}</p>
          <p class="tx-meta">${formatRelativeDate(tx.fecha)} · ${tx.categoria} › ${tx.subcategoria}</p>
        </div>
        <div class="tx-right">
          <span class="tx-amount ${cls}">${sign} $${formatCurrency(tx.monto)}</span>
          <span class="sync-badge" title="${tx.syncStatus}">${badge}</span>
          <button class="btn-icon btn-edit" data-id="${tx.id}" title="Editar">✏️</button>
          <button class="btn-icon btn-del"  data-id="${tx.id}" title="Eliminar">🗑️</button>
        </div>
      </div>
    `;
  }

  private bindTxActions() {
    this.querySelectorAll<HTMLButtonElement>('.btn-edit').forEach(btn => {
      btn.addEventListener('click', () => this.startEdit(btn.dataset.id!));
    });
    this.querySelectorAll<HTMLButtonElement>('.btn-del').forEach(btn => {
      btn.addEventListener('click', () => this.confirmDelete(btn.dataset.id!));
    });
  }

  private startEdit(id: string) {
    const tx = getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll().find(t => t.id === id);
    if (!tx) return;
    this._editingId = id;
    this.setTipo(tx.tipo);
    (this.querySelector('#fMonto') as HTMLInputElement).value = String(tx.monto);
    (this.querySelector('#fDesc') as HTMLInputElement).value  = tx.descripcion;
    const catSel = this.querySelector<HTMLSelectElement>('#fCat')!;
    catSel.value = tx.categoria;
    this.updateSubs();
    (this.querySelector('#fSub') as HTMLSelectElement).value = tx.subcategoria;
    this.setDate((tx.fecha ?? '').slice(0, 10) || this.todayInputValue());
    (this.querySelector('#btnSave') as HTMLButtonElement).textContent = 'Guardar cambios';
    this.setStatus(`Editando: ${tx.descripcion || tx.subcategoria}`);
    this.scrollIntoView({ behavior: 'smooth' });
  }

  private confirmDelete(id: string) {
    const tx = getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll().find(t => t.id === id);
    if (!tx) return;
    this._modal.open({
      title:        'Eliminar movimiento',
      body:         `¿Eliminar "${tx.descripcion || tx.subcategoria}" de $${formatCurrency(tx.monto)}?`,
      confirmLabel: 'Eliminar',
      danger:       true,
      onConfirm:    () => getModule<{ remove: (id: string) => Promise<void> }>('transactions')?.remove(id),
    });
  }

  private resetForm() {
    this._editingId = null;
    (this.querySelector('#txForm') as HTMLFormElement).reset();
    this.setTipo('Gasto');
    this.setDate(this.todayInputValue());
    this.updateSubs();
    (this.querySelector('#btnSave') as HTMLButtonElement).textContent = 'Guardar';
    this.setStatus('Tocá el micrófono o escribí en lenguaje natural');
  }
}

// ── Panel Historial ───────────────────────────────────────────────────────────

const PERIOD_LABELS: { value: string; label: string }[] = [
  { value: 'mes_actual', label: 'Este mes' },
  { value: 'semana',     label: 'Última semana' },
  { value: 'mes',        label: 'Último mes' },
  { value: 'trimestre',  label: 'Último trimestre' },
  { value: 'anio',       label: 'Último año' },
  { value: 'todo',       label: 'Todo' },
];

const PAGE_SIZE = 20;

export class AppPanelHistorial extends HTMLElement {
  private _period  = 'mes_actual';
  private _tipo    = 'todos';
  private _cat     = 'todas';
  private _search  = '';
  private _limit   = PAGE_SIZE;
  private _modal!: AppModal;

  static define() { customElements.define('app-panel-historial', AppPanelHistorial); }

  connectedCallback() {
    this.render();
    this._modal = this.querySelector('app-modal') as AppModal;
    this.bindFilters();
    this.populateCats();
    this.refresh();

    useBus('transactions:loaded',  () => this.refresh(), this);
    useBus('transactions:saved',   () => this.refresh(), this);
    useBus('transactions:updated', () => this.refresh(), this);
    useBus('transactions:deleted', () => this.refresh(), this);
    useBus('categories:updated',   () => this.populateCats(), this);
  }

  private render() {
    this.innerHTML = `
      <style>
        .hist-title { font-family: var(--font-display); font-size: var(--text-xl);
                      font-weight: var(--font-weight-bold); margin-bottom: var(--space-4); }
        .hist-filters { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-2);
                        margin-bottom: var(--space-3); }
        .hist-filters .full { grid-column: 1 / -1; }
        .hist-field label { font-size: var(--text-xs); color: var(--color-text-muted);
                            display: block; margin-bottom: var(--space-1); }
        .hist-field select, .hist-field input {
          width: 100%; box-sizing: border-box;
          background: var(--color-bg); border: 1px solid var(--color-border);
          border-radius: var(--radius-md); padding: var(--space-2) var(--space-3);
          color: var(--color-text); font-family: var(--font-body); font-size: var(--text-sm);
        }
        .hist-field select:focus, .hist-field input:focus { outline: none; border-color: var(--color-border-focus); }

        .hist-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--space-2);
                        margin-bottom: var(--space-4); }
        .hist-stat { background: var(--color-surface); border: 1px solid var(--color-border);
                     border-radius: var(--radius-md); padding: var(--space-3); text-align: center; }
        .hist-stat .lbl { font-size: var(--text-xs); color: var(--color-text-muted); margin-bottom: 2px; }
        .hist-stat .val { font-family: var(--font-display); font-weight: var(--font-weight-semibold);
                          font-variant-numeric: tabular-nums; font-size: var(--text-sm); }
        .hist-stat .val.ingreso { color: var(--color-income); }
        .hist-stat .val.gasto   { color: var(--color-expense); }
        .hist-stat .val.pos     { color: var(--color-balance-pos); }
        .hist-stat .val.neg     { color: var(--color-balance-neg); }

        .hist-count { font-size: var(--text-xs); color: var(--color-text-muted); margin-bottom: var(--space-2); }

        .hist-item { display: flex; justify-content: space-between; align-items: center;
                     padding: var(--space-3); border-radius: var(--radius-md);
                     background: var(--color-bg); margin-bottom: var(--space-2);
                     border: 1px solid var(--color-border); }
        .hist-left  { flex: 1; min-width: 0; }
        .hist-desc  { font-weight: var(--font-weight-medium); font-size: var(--text-sm);
                      white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .hist-meta  { font-size: var(--text-xs); color: var(--color-text-muted); margin-top: 2px; }
        .hist-right { display: flex; align-items: center; gap: var(--space-2); flex-shrink: 0; }
        .hist-amount { font-family: var(--font-display); font-weight: var(--font-weight-semibold);
                       font-variant-numeric: tabular-nums; font-size: var(--text-base); }
        .hist-amount.gasto   { color: var(--color-expense); }
        .hist-amount.ingreso { color: var(--color-income); }
        .hist-badge { font-size: 0.65rem; }
        .hist-del { background: none; border: none; cursor: pointer; padding: var(--space-1);
                    border-radius: var(--radius-sm); color: var(--color-text-muted);
                    transition: color var(--duration-fast); font-size: 0.9rem; }
        .hist-del:hover { color: var(--color-expense); }

        .hist-more { width: 100%; margin: var(--space-2) 0 var(--space-8);
                     background: var(--color-surface); color: var(--color-text);
                     border: 1px solid var(--color-border); border-radius: var(--radius-md);
                     padding: var(--space-3); font-family: var(--font-body); font-size: var(--text-sm);
                     font-weight: var(--font-weight-medium); cursor: pointer; }
        .hist-more:hover { background: var(--color-surface-raised); }
        .hist-bottom-space { height: var(--space-12); }
      </style>

      <h2 class="hist-title">Historial</h2>

      <div class="hist-filters">
        <div class="hist-field">
          <label>Período</label>
          <select id="hPeriod">
            ${PERIOD_LABELS.map(p => `<option value="${p.value}">${p.label}</option>`).join('')}
          </select>
        </div>
        <div class="hist-field">
          <label>Tipo</label>
          <select id="hTipo">
            <option value="todos">Todos</option>
            <option value="Gasto">Gastos</option>
            <option value="Ingreso">Ingresos</option>
          </select>
        </div>
        <div class="hist-field">
          <label>Categoría</label>
          <select id="hCat"></select>
        </div>
        <div class="hist-field">
          <label>Buscar</label>
          <input type="search" id="hSearch" placeholder="Descripción, categoría…" enterkeyhint="search">
        </div>
      </div>

      <div class="hist-summary">
        <div class="hist-stat"><div class="lbl">Ingresos</div><div class="val ingreso" id="hIng">$0</div></div>
        <div class="hist-stat"><div class="lbl">Gastos</div><div class="val gasto" id="hGas">$0</div></div>
        <div class="hist-stat"><div class="lbl">Balance</div><div class="val" id="hBal">$0</div></div>
      </div>

      <p class="hist-count" id="hCount"></p>
      <div id="hList"></div>

      <app-modal></app-modal>
    `;
  }

  private bindFilters() {
    const onChange = () => { this._limit = PAGE_SIZE; this.readFilters(); this.refresh(); };
    this.querySelector('#hPeriod')!.addEventListener('change', onChange);
    this.querySelector('#hTipo')!.addEventListener('change', onChange);
    this.querySelector('#hCat')!.addEventListener('change', onChange);
    this.querySelector('#hSearch')!.addEventListener('input', onChange);
  }

  private readFilters() {
    this._period = (this.querySelector('#hPeriod') as HTMLSelectElement).value;
    this._tipo   = (this.querySelector('#hTipo') as HTMLSelectElement).value;
    this._cat    = (this.querySelector('#hCat') as HTMLSelectElement).value;
    this._search = (this.querySelector('#hSearch') as HTMLInputElement).value.trim().toLowerCase();
  }

  private populateCats() {
    const cats = getModule<{ getAll: () => Record<string, string[]> }>('categories')?.getAll() ?? {};
    const sel  = this.querySelector<HTMLSelectElement>('#hCat');
    if (!sel) return;
    const current = sel.value || 'todas';
    sel.innerHTML = `<option value="todas">Todas</option>` +
      Object.keys(cats).map(c => `<option value="${c}">${c}</option>`).join('');
    sel.value = current;
  }

  private filtered(): Transaction[] {
    const all = getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll() ?? [];
    const { desde, hasta } = getPeriodDates(this._period);
    return all
      .filter(tx => {
        const d = parseDate(tx.fecha);
        if (d < desde || d > hasta) return false;
        if (this._tipo !== 'todos' && tx.tipo !== this._tipo) return false;
        if (this._cat  !== 'todas' && tx.categoria !== this._cat) return false;
        if (this._search) {
          const hay = `${tx.descripcion} ${tx.categoria} ${tx.subcategoria}`.toLowerCase();
          if (!hay.includes(this._search)) return false;
        }
        return true;
      })
      .sort((a, b) => parseDate(b.fecha).getTime() - parseDate(a.fecha).getTime());
  }

  private refresh() {
    const rows = this.filtered();

    // Resumen sobre el conjunto filtrado completo
    let ing = 0, gas = 0;
    for (const tx of rows) (tx.tipo === 'Ingreso' ? (ing += tx.monto) : (gas += tx.monto));
    const bal = ing - gas;

    (this.querySelector('#hIng') as HTMLElement).textContent = `$${formatCurrency(ing)}`;
    (this.querySelector('#hGas') as HTMLElement).textContent = `$${formatCurrency(gas)}`;
    const balEl = this.querySelector('#hBal') as HTMLElement;
    balEl.textContent = `$${formatCurrency(bal)}`;
    balEl.classList.toggle('pos', bal >= 0);
    balEl.classList.toggle('neg', bal < 0);

    const countEl = this.querySelector('#hCount') as HTMLElement;
    const list    = this.querySelector('#hList') as HTMLElement;

    if (!rows.length) {
      countEl.textContent = '';
      list.innerHTML = '<app-empty-state icon="🔍" message="Sin movimientos" sub="Probá cambiar los filtros"></app-empty-state>';
      return;
    }

    const shown = rows.slice(0, this._limit);
    countEl.textContent = `Mostrando ${shown.length} de ${rows.length}`;
    list.innerHTML =
      shown.map(tx => this.itemHtml(tx)).join('') +
      (rows.length > this._limit ? `<button class="hist-more" id="hMore">Mostrar más</button>` : '') +
      `<div class="hist-bottom-space"></div>`;

    this.querySelector('#hMore')?.addEventListener('click', () => {
      this._limit += PAGE_SIZE;
      this.refresh();
    });
    this.querySelectorAll<HTMLButtonElement>('.hist-del').forEach(btn =>
      btn.addEventListener('click', () => this.confirmDelete(btn.dataset.id!)));
  }

  private itemHtml(tx: Transaction): string {
    const sign  = tx.tipo === 'Gasto' ? '−' : '+';
    const cls   = tx.tipo === 'Gasto' ? 'gasto' : 'ingreso';
    const badge = tx.syncStatus === 'synced' ? '☁️✓' : tx.syncStatus === 'pending' ? '☁️⚠' : tx.syncStatus === 'error' ? '☁️✕' : '☁️⟳';
    return `
      <div class="hist-item" data-id="${tx.id}">
        <div class="hist-left">
          <p class="hist-desc">${tx.descripcion || tx.subcategoria || tx.categoria}</p>
          <p class="hist-meta">${formatRelativeDate(tx.fecha)} · ${tx.categoria}${tx.subcategoria ? ' › ' + tx.subcategoria : ''}</p>
        </div>
        <div class="hist-right">
          <span class="hist-amount ${cls}">${sign} $${formatCurrency(tx.monto)}</span>
          <span class="hist-badge" title="${tx.syncStatus}">${badge}</span>
          <button class="hist-del" data-id="${tx.id}" title="Eliminar">🗑️</button>
        </div>
      </div>
    `;
  }

  private confirmDelete(id: string) {
    const tx = getModule<{ getAll: () => Transaction[] }>('transactions')?.getAll().find(t => t.id === id);
    if (!tx) return;
    this._modal.open({
      title:        'Eliminar movimiento',
      body:         `¿Eliminar "${tx.descripcion || tx.subcategoria}" de $${formatCurrency(tx.monto)}?`,
      confirmLabel: 'Eliminar',
      danger:       true,
      onConfirm:    () => getModule<{ remove: (id: string) => Promise<void> }>('transactions')?.remove(id),
    });
  }
}
